import React, { useState } from 'react';
import { useQuoteStore } from '@/store/quoteStore';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Info, Minus, Plus } from 'lucide-react';

const Step4Mesh = () => {
  const { requiresMesh, setRequiresMesh, meshRolls, setMeshRolls, setCurrentStep, getMeshRolls } = useQuoteStore();
  const [selected, setSelected] = useState(requiresMesh ?? null);
  const calculatedMeshRolls = getMeshRolls();
  const actualMeshRolls = meshRolls || calculatedMeshRolls;

  const handleNext = () => {
    if (selected !== null) {
      setRequiresMesh(selected);
      if (!selected) {
        setMeshRolls(0);
      } else if (!meshRolls) {
        setMeshRolls(calculatedMeshRolls);
      }
      setCurrentStep(5);
    }
  };

  const handleBack = () => {
    setCurrentStep(3);
  };

  const handleMeshSelection = (value) => {
    setSelected(value);
    if (value) {
      if (!meshRolls) {
        setMeshRolls(calculatedMeshRolls || 1);
      }
    }
  };

  const incrementMeshRolls = () => {
    setMeshRolls((actualMeshRolls || calculatedMeshRolls) + 1);
  };

  const decrementMeshRolls = () => {
    const currentRolls = actualMeshRolls || calculatedMeshRolls;
    if (currentRolls > 0) {
      setMeshRolls(currentRolls - 1);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
      <h2 className="text-2xl md:text-3xl font-bold text-[#27292E] mb-2">
        Structural Reinforcement Mesh
      </h2>
      <p className="text-gray-600 mb-6">
        Do you require structural reinforcement mesh for your project?
      </p>

      <div className="bg-[#E0E9EB] border-l-4 border-[#2F5E96] rounded-lg p-4 mb-8">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-[#2F5E96] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700">
            <p className="mb-2">
              <strong>Note:</strong> Each roll covers 100m².
            </p>
            {calculatedMeshRolls > 0 && (
              <p className="text-[#2F5E96] font-bold">
                Based on your project size, you will need {calculatedMeshRolls} roll{calculatedMeshRolls > 1 ? 's' : ''}.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div
          className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
            selected === true
              ? 'border-[#2F5E96] bg-[#2F5E96]/5'
              : 'border-gray-200 hover:border-[#2F5E96]/50'
          }`}
          onClick={() => handleMeshSelection(true)}
          data-testid="mesh-yes"
        >
          <div>
            <div className="text-lg font-bold text-[#27292E]">Yes</div>
            <div className="text-sm text-gray-500">Include reinforcement mesh</div>
          </div>
          
          {selected === true && (
            <div className="mt-4 pt-4 border-t-2 border-gray-200">
              <Label className="text-sm font-medium text-gray-700 mb-3 block">
                Number of Rolls
              </Label>
              <div className="flex items-center gap-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    decrementMeshRolls();
                  }}
                  className="w-12 h-12 flex items-center justify-center bg-white border-2 border-[#2F5E96] text-[#2F5E96] rounded-lg hover:bg-[#2F5E96]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="mesh-decrement"
                  disabled={actualMeshRolls <= 0}
                >
                  <Minus className="w-5 h-5" />
                </button>
                
                <div className="flex-1 text-center">
                  <div className="text-3xl font-bold text-[#2F5E96]" data-testid="mesh-quantity">
                    {actualMeshRolls}
                  </div>
                  <div className="text-sm text-gray-500">roll{actualMeshRolls > 1 ? 's' : ''}</div>
                </div>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    incrementMeshRolls();
                  }}
                  className="w-12 h-12 flex items-center justify-center bg-[#2F5E96] text-white rounded-lg hover:bg-[#254b7a] transition-colors"
                  data-testid="mesh-increment"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>

        <div
          className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
            selected === false
              ? 'border-[#2F5E96] bg-[#2F5E96]/5'
              : 'border-gray-200 hover:border-[#2F5E96]/50'
          }`}
          onClick={() => handleMeshSelection(false)}
          data-testid="mesh-no"
        >
          <div>
            <div className="text-lg font-bold text-[#27292E]">No</div>
            <div className="text-sm text-gray-500">No reinforcement mesh needed</div>
          </div>
        </div>
      </div>

      <div className="flex gap-4 mt-8">
        <Button
          onClick={handleBack}
          variant="outline"
          className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#2F5E96] text-[#2F5E96] hover:bg-[#2F5E96]/5"
          data-testid="back-button-step4"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>
        <Button
          onClick={handleNext}
          disabled={selected === null}
          className="flex-1 bg-[#2F5E96] hover:bg-[#254b7a] text-white h-14 text-lg font-bold rounded-xl transition-transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="next-button-step4"
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default Step4Mesh;